import { usePublishingCompletion } from 'components/CommonComponents/PublishingLoader/usePublishingCompletion'
import useLocaleText from 'components/useLocaleText'
import { aiSubmissionText } from './submissionLiterals'
import { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '@chakra-ui/core'
import { TaskFormData, TaskAudience } from 'components/TasksV2/TaskCreation/FormStepContext'
import { AppDispatch } from 'config/redux'
import { RootState } from 'constants/interfaces'
import { captureEvent } from 'services/posthog'
import { createAiTaskApi } from '../api/client'
import { buildAiTaskPayload, computeBriefingFireTs, buildPopJob, AiTaskDraft } from '../model/submission'

/** Owns the existing six-stage publish lifecycle; the provider only owns editable form/navigation state. */
export function useAiTaskSubmission(getFormData: () => Partial<TaskFormData>, onClose?: VoidFunction) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { publicationComplete, resetCompletion, showCompletion } = usePublishingCompletion()

  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()
  const rid = useSelector((state: RootState) => state.config.config.rid)
  const uid = useSelector((state: RootState) => state.auth.uid)
  const retailUsers = useSelector((state: RootState) => state.config.config.retailUsersObject ?? {})
  const submitting = useRef(false)
  const labels = useLocaleText(aiSubmissionText)
  const labelsRef = useRef(labels)
  labelsRef.current = labels

  const submitAllForms = useCallback(async () => {
    if (submitting.current) return
    submitting.current = true
    const t = labelsRef.current
    const api = createAiTaskApi(dispatch)
    const allData: AiTaskDraft = getFormData()
    const { title = '' } = allData.SUBTASK_TASK_CREATION || {}
    const settings = allData.TASK_SETTINGS
    const { audience, watchers, auditors, send_action_pop = true } = settings || {}
    const { groups, users, tags } = (audience as TaskAudience) || { groups: [], users: [], tags: [] }
    resetCompletion()
    setIsSubmitting(true)
    let shouldReleaseTaskLoading = false
    let taskCreated = false
    let setupComplete = false
    let setupStage = t.briefingStage
    try {
      // 1. Create prompt → get message_id
      const promptText = allData.AI_PROMPT?.prompt || ''
      const briefingUsers = Array.from(new Set([...(uid ? [uid] : []), ...(watchers || []), ...(auditors || [])]))
      const promptId = await api.createPrompt({
        prompt: promptText,
        users,
        groups,
        tags,
        data: { title: '', content: title },
      })

      if (!promptId) {
        toast({
          title: t.promptFailed,
          description: t.promptRetry,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      const payload = buildAiTaskPayload(allData, promptId)
      // 2. Create task → get tid
      shouldReleaseTaskLoading = true
      const tid = await api.createTask(payload)
      taskCreated = !!tid
      if (!tid) {
        toast({
          title: t.taskFailed,
          description: t.taskRetry,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      const stoAiUser = retailUsers['sto-ai']
      const profileImageUrl = stoAiUser?.profile_img_url ?? null
      // 3. Create AI briefing pop → get pop_id

      const pop_id = await api.createBriefing({
        tid,
        title,
        groups: [],
        users: briefingUsers,
        tags: [],
        profileImageUrl,
      })
      if (!pop_id) {
        toast({
          title: t.briefingFailed,
          description: t.briefingPartial.replace('{title}', title),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      // The briefing offset and action-pop task-start schedule are deliberately different.
      const scheduleStartTs = computeBriefingFireTs(
        payload.from_ts,
        payload.deadline,
        settings?.ai_schedule?.offsetMinutes ?? null
      )
      setupStage = t.briefingJobStage
      const jobOk = await api.createJob(buildPopJob({ popId: pop_id, tid, rid, title, scheduleStartTs }))
      if (!jobOk) {
        toast({
          title: t.jobFailed,
          description: t.briefingJobPartial.replace('{title}', title),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      // 5. Create Action Pop → get action_pop_id (skipped if send_action_pop is disabled)
      if (!send_action_pop) {
        setupComplete = true
        return
      }
      const refPopTitle = 'ref_pop_title'
      setupStage = t.actionStage
      const action_pop_id = await api.createAction({ title: refPopTitle, groups, users, tags })
      if (!action_pop_id) {
        toast({
          title: t.actionFailed,
          description: t.actionPartial.replace('{title}', title),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      // 6. Create Job for Action Pop — fires at task start, not briefing offset
      setupStage = t.actionJobStage
      const actionJobOk = await api.createJob(
        buildPopJob({ popId: action_pop_id, tid, rid, title, scheduleStartTs: payload.from_ts })
      )
      if (!actionJobOk) {
        toast({
          title: t.actionJobFailed,
          description: t.actionJobPartial.replace('{title}', title),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }
      setupComplete = true
    } catch (e) {
      console.error('AI Task creation failed:', e)
      toast({
        title: taskCreated ? t.setupIncomplete : t.taskFailed,
        description: taskCreated
          ? t.stagePartial.replace('{title}', title).replace('{stage}', setupStage)
          : t.unexpectedRetry,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      if (shouldReleaseTaskLoading) {
        api.releaseLoading()
      }
      const stillActive = setupComplete ? await showCompletion() : true
      // Once createTask succeeds, a retry would publish a duplicate. Keep the task and report
      // incomplete follow-up setup, just as for an action-pop failure after successful creation.
      if (taskCreated) {
        captureEvent('ai_task_published')
        api.complete()
        if (stillActive) onClose?.()
      }
      submitting.current = false
      setIsSubmitting(false)
    }
  }, [dispatch, getFormData, onClose, toast, rid, uid, retailUsers, resetCompletion, showCompletion])

  return { isSubmitting, submitAllForms, publicationComplete }
}
